import { useCallback, useEffect, useRef, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Confetti from "react-confetti";
import "./index.css";

import { Moon, Sun } from "lucide-react";
import bronze from "./assets/ranks/bronze.png";
import challenger from "./assets/ranks/challenger.png";
import diamond from "./assets/ranks/diamond.png";
import emerald from "./assets/ranks/emerald.png";
import gold from "./assets/ranks/gold.png";
import grandmaster from "./assets/ranks/grandmaster.png";
import iron from "./assets/ranks/iron.png";
import master from "./assets/ranks/master.png";
import platinum from "./assets/ranks/platinum.png";
import silver from "./assets/ranks/silver.png";
import { ResultModal } from "./components/ResultModal";


export function Game() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [image, setImage] = useState<number | null>(null);
  const [theme, setTheme] = useState("dark");
  const [selectedRank, setSelectedRank] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<any | null>(null);
  const [round, setRound] = useState(1);
  const [totalScore, setTotalScore] = useState(0);
  const [history, setHistory] = useState<any[]>([]);
  const [gameOver, setGameOver] = useState(false);

  const initialized = useRef(false);

  const ranksList = [
    "Iron", "Bronze", "Silver", "Gold", "Platinum", "Emerald", "Diamond", "Master", "Grandmaster", "Challenger"
  ];

  const calculateScore = (guessedRank: string, actualRank: string) => {
    const guessedIndex = ranksList.findIndex(r => r.toLowerCase() === guessedRank.toLowerCase());
    const actualIndex = ranksList.findIndex(r => r.toLowerCase() === actualRank.toLowerCase());
    const distance = Math.abs(guessedIndex - actualIndex);

    if (distance === 0) return 10;
    if (distance === 1) return 5;
    if (distance === 2) return 3;
    if (distance === 3) return 1;
    return 0;
  };

  const loadNewImage = useCallback(() => {
    fetch("/api/image-count")
      .then(response => response.json())
      .then(data => {
        const newImageId = Math.floor(Math.random() * data.count) + 1;
        setImage(newImageId);
      });
  }, []);

  const validateAnswer = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error("executeRecaptcha is not available");
      return;
    }

    try {
      const token = await executeRecaptcha("validate");

      const response = await fetch("/api/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          selectedRank,
          imageID: image,
        }),
      });

      const data = await response.json();

      const points = calculateScore(selectedRank!, data.correct);
      setTotalScore(prev => prev + points);

      setLastResult({ ...data, points });
      setHistory(prev => [...prev, { image: image, guess: selectedRank, correct: data.correct, points }]);
    } catch (error) {
      console.error("Error validating answer:", error);
    }
  }, [executeRecaptcha, selectedRank, image]);

  const handleNextRound = useCallback(() => {
    setSelectedRank(null);
    setLastResult(null);

    if (round < 3) {
      setRound(prev => prev + 1);
      loadNewImage();
    } else {
      setGameOver(true);
    }
  }, [round, loadNewImage]);

  const restartGame = useCallback(() => {
    setRound(1);
    setTotalScore(0);
    setHistory([]);
    setGameOver(false);
    setSelectedRank(null);
    setLastResult(null);
    loadNewImage();
  }, [loadNewImage]);

  const ranks = [
    { name: "Iron", icon: iron },
    { name: "Bronze", icon: bronze },
    { name: "Silver", icon: silver },
    { name: "Gold", icon: gold },
    { name: "Platinum", icon: platinum },
    { name: "Emerald", icon: emerald },
    { name: "Diamond", icon: diamond },
    { name: "Master", icon: master },
    { name: "Grandmaster", icon: grandmaster },
    { name: "Challenger", icon: challenger },
  ];

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      loadNewImage();
    }
  }, [loadNewImage]);

  if (gameOver) {
    return (
      <div className="container p-8 text-center relative z-10">
        <div className="flex justify-center items-center gap-8 mb-8 text-3xl font-bold">
          Game Over
        </div>
        <div className="mb-8">
          <h2 className="text-4xl font-bold mb-4">Total Score: {totalScore}</h2>
          <div className="space-y-4 w-full md:w-md mx-auto">
            {history.map((round, index) => (
              <div key={index} className="bg-card p-4 rounded-lg flex justify-between items-center border">
                <span>Round {index + 1}</span>
                <div className="flex flex-col items-end">
                  <span className="font-bold">{round.points} pts</span>
                  <span className="text-sm text-muted-foreground">{round.guess} (Actual: {round.correct})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={restartGame}
          className="btn w-48 btn-primary rounded-full"
        >
          Play Again
        </button>
      </div>
    );
  }

  return image ? (
    <>
      <div className="absolute top-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-full hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer text-foreground"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun size={24} /> : <Moon size={24} />}
        </button>
      </div>

      <div className="container p-8 text-center relative z-10">
        <div className="flex justify-between items-center mb-8">
          <div className="text-xl font-bold">Round {round} / 3</div>
          <div className="text-3xl font-bold">Chat Restrict</div>
          <div className="text-xl font-bold">Score: {totalScore}</div>
        </div>

        <div className="flex justify-center items-center gap-8 mb-8 flex-col">
          <p>Guess the elo of that game based on the in-game chat!</p>
          <img src={`/img/chat-${image}.png`} alt="In-game chat" className="md:h-96 h-64 object-contain select-none pointer-events-none" />
        </div>

        <div className="flex justify-center items-center md:gap-8 md:mb-8 gap-2 mb-4 flex-row flex-wrap">
          {ranks.map((rank) => (
            <img
              key={rank.name}
              src={rank.icon}
              alt={rank.name}
              width={64}
              onClick={() => setSelectedRank(rank.name)}
              className={`p-2 cursor-pointer ${selectedRank === rank.name ? "bg-accent" : ""} hover:bg-accent hover:text-accent-foreground transition-colors rounded-full`}
            />
          ))}
        </div>

        <button
          onClick={validateAnswer}
          className="btn w-32 btn-primary rounded-full"
        >
          Submit
        </button>
      </div>

      <ResultModal
        isOpen={!!lastResult}
        success={lastResult?.success}
        correct={lastResult?.correct}
        points={lastResult?.points || 0}
        currentRound={round}
        totalRounds={3}
        stats={lastResult}
        onPlayAgain={handleNextRound}
      />
    </>
  ) : (
    <div></div>
  );
}

export default Game;
